import React, { forwardRef, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { selectedSearchAtom } from "../../jotai/atoms";
import { ModalV2, ModalV2BackButton, ModalV2Body, ModalV2Header } from "../modals/ModalV2";
import { DeleteSearchButton } from "../DeleteSearchButton";
import { SearchForm, SearchFormProps } from "./SearchForm";

type AdultChildTab = "adult" | "child";

interface SearchModalProps {
  onRunSearch: SearchFormProps["onRunSearch"];
  onDelete?: () => void;
}

export const SearchModal = forwardRef<HTMLDialogElement, SearchModalProps>(function SearchModal(
  { onRunSearch, onDelete },
  ref
) {
  const uniqueId = useId();
  const radioGroupName = `adult_child_tabs_${uniqueId}`;
  const { t } = useTranslation();
  const [curSearch, setCurSearch] = useAtom(selectedSearchAtom);
  const [adultChildTab, setAdultChildTab] = useState<AdultChildTab>("adult");

  useEffect(() => {
    if (curSearch) {
      setAdultChildTab(curSearch.childFirstName ? "child" : "adult");
    }
  }, [curSearch]);

  return (
    <ModalV2 modalBoxClassName="bg-base-100 max-w-3xl" ref={ref} onClose={() => setCurSearch(null)}>
      <div>
        <ModalV2Header className="flex items-center justify-between py-2">
          <ModalV2BackButton />
          <DeleteSearchButton onDelete={onDelete} />
        </ModalV2Header>
        <ModalV2Header className="flex items-center !h-fit">
          <div className="tabs tabs-lift translate-y-0.5 flex-nowrap w-full">
            {/*hidden first tab to force the second tab to curve on its left side */}
            <input type="radio" name={radioGroupName} className="tab hidden" aria-label="" />
            <input
              type="radio"
              value="adult"
              name={radioGroupName}
              className=" tab [--tab-border-color:var(--color-base-100)]  text-secondary-content checked:text-primary font-bold text-lg"
              onChange={(e) => setAdultChildTab(e.target.value as AdultChildTab)}
              checked={adultChildTab === "adult"}
              aria-label={t("immunizations.search_adult_records")}
            />
            <input
              type="radio"
              name={radioGroupName}
              value="child"
              className=" tab [--tab-border-color:var(--color-base-100)]  text-secondary-content checked:text-primary font-bold text-lg"
              onChange={(e) => setAdultChildTab(e.target.value as AdultChildTab)}
              checked={adultChildTab === "child"}
              aria-label={t("immunizations.search_child_records")}
            />
            {/*hidden fourth tab to force the third tab to curve on its right side */}
            <input type="radio" name={radioGroupName} className="tab hidden" aria-label="" />
          </div>
        </ModalV2Header>
      </div>
      <ModalV2Body className="bg-base-100">
        <SearchForm adultChild={adultChildTab} onRunSearch={onRunSearch}></SearchForm>
      </ModalV2Body>
    </ModalV2>
  );
});

export default SearchModal;
